import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';
import { checkIfPmi } from '@/utils/commonUtils';
import { Renter, RenterInviteType } from '@/utils/types/renter.interface';
import type { User } from '@/utils/types/User';
import { ScreeningResultsTypes } from '../ScreeningResultsTypes';

const applyNewFormDefaults = (payload: Renter) => {
  payload.renterInviteType = RenterInviteType.SMBInviteNewLimitedSdr;

  // When the landlord's default coverage already includes baseline SDR
  // (sdr_months > 0), skip the customize override and submit as the
  // default flow. Otherwise the (sdr_months + 1) * rent calculation below
  // would double-count against their existing baseline.
  const sdrMonths = payload.selectedSmbPricing?.sdr_months;
  const skipCustomizeOverride =
    payload.includeDepositCoverage && sdrMonths !== undefined && sdrMonths > 0;

  if (payload.includeDepositCoverage && !skipCustomizeOverride) {
    /**
     * The SMB ON path still submits the monolith's "custom coverage" shape for
     * SDR/LG, but the values now come from DDS pricing instead of the old static
     * month radios.
     */
    payload.rentCoverage = RentCoverageTypes.Customize;
    payload.securityDepositCoverage = SecurityDepositTypes.CustomizeOrRemove;
    payload.customCoverage = true;

    if (payload.selectedSmbPricing) {
      payload.monthsOfCoverage = Math.max(0, payload.selectedSmbPricing.lg_months - 1);
      payload.securityDepositReplacementAmount =
        (payload.selectedSmbPricing.sdr_months + 1) * (payload.grossMonthlyRent ?? 0);
      return;
    }

    if (payload.monthsOfCoverage) {
      payload.monthsOfCoverage = Math.max(0, Number(payload.monthsOfCoverage) - 1);
    }
    payload.securityDepositReplacementAmount = payload.grossMonthlyRent ?? 0;
    return;
  }

  /**
   * Default SMB submits no longer need explicit coverage amounts. Screening
   * status alone identifies the selected DDS-backed default option.
   */
  payload.rentCoverage = RentCoverageTypes.CompanyDefault;
  payload.securityDepositCoverage = SecurityDepositTypes.CompanyDefault;
  payload.customCoverage = false;
  delete payload.monthsOfCoverage;
  delete payload.securityDepositReplacementAmount;
};

const applyLegacyFormDefaults = (
  payload: Renter,
  user: User | null | undefined,
  isSmbRenterInvites: boolean,
) => {
  if (user && !checkIfPmi(user) && isSmbRenterInvites) {
    payload.renterInviteType = RenterInviteType.SMBInviteOldCustomCoverage;
  }
  if (payload.rentCoverage === RentCoverageTypes.CompanyDefault) {
    delete payload.monthsOfCoverage;
  }
  if (payload.securityDepositCoverage === SecurityDepositTypes.CompanyDefault) {
    delete payload.securityDepositReplacementAmount;
  }
};

export const normalizeRenterInviteValues = (
  values: Renter,
  useNewForm: boolean,
  user: User | null | undefined,
  isSmbRenterInvites: boolean,
): Renter => {
  const payload = { ...values };
  if (useNewForm) {
    applyNewFormDefaults(payload);
  } else {
    applyLegacyFormDefaults(payload, user, isSmbRenterInvites);
  }
  if (payload.customCoverage || (isSmbRenterInvites && payload.includeDepositCoverage)) {
    payload.approvalStatus = ScreeningResultsTypes.Denied;
  }
  return payload;
};

export const buildLegacyRequestPayload = (values: Renter): Record<string, unknown> => {
  const payload = { ...values };
  delete payload.selectedSmbPricing;
  const { address, ...rest } = payload;
  return {
    ...rest,
    building: { monolith_building_ids: [address.monolith_building_id] },
  };
};
